import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  useQuoteList,
  QUOTE_STATUS_LABELS,
  QUOTE_STATUSES,
  type QuoteStatus,
} from '@/hooks/useQuoteContract';
import {
  QUOTE_CONTRACT_PERMISSIONS,
  canSeeQuoteContractMenu,
  hasPermission,
} from '@/lib/quoteContractPermissions';

const BADGE: Record<QuoteStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  submitted: 'bg-blue-100 text-blue-800',
  price_review_approved: 'bg-green-100 text-green-800',
  contract_generated: 'bg-emerald-100 text-emerald-900',
  changes_requested: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-zinc-200 text-zinc-700',
  superseded: 'bg-zinc-200 text-zinc-700',
};

export default function QuoteListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canRead = canSeeQuoteContractMenu(permissions);
  const canCreate = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.CREATE,
  );
  const { rows, total, page, limit, loading, error, fetchQuotes } =
    useQuoteList();

  const [filterStatus, setFilterStatus] = useState<QuoteStatus | ''>('');
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterProposalId, setFilterProposalId] = useState('');
  const [search, setSearch] = useState('');
  const [pageInput, setPageInput] = useState(1);

  useEffect(() => {
    if (!canRead) return;
    void fetchQuotes({
      status: filterStatus || undefined,
      customerId: filterCustomerId || undefined,
      proposalId: filterProposalId || undefined,
      search: search || undefined,
      page: pageInput,
      limit: 20,
    });
  }, [
    filterStatus,
    filterCustomerId,
    filterProposalId,
    search,
    pageInput,
    canRead,
    fetchQuotes,
  ]);

  if (!canRead) return <AccessDenied resource="报价 / 合同" />;

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">正式报价</h1>
          <p className="text-sm text-muted-foreground">
            草稿、提交至财务复核、复核通过、合同生成等阶段的报价记录。
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate('/quote-contract/quotes/create')}>
            新建报价
          </Button>
        )}
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="status">状态</Label>
            <select
              id="status"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus((e.target.value as QuoteStatus | '') || '')
              }
            >
              <option value="">全部</option>
              {QUOTE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {QUOTE_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="customer">客户 ID</Label>
            <Input
              id="customer"
              value={filterCustomerId}
              onChange={(e) => setFilterCustomerId(e.target.value)}
              placeholder="按客户 ID 过滤"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="proposal">方案 ID</Label>
            <Input
              id="proposal"
              value={filterProposalId}
              onChange={(e) => setFilterProposalId(e.target.value)}
              placeholder="按方案 ID 过滤"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="search">标题搜索</Label>
            <Input
              id="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="按报价标题过滤"
            />
          </div>
        </div>
      </Card>

      {error && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          加载失败：{error.message}
        </Card>
      )}

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">报价编号</th>
              <th className="px-3 py-2 text-left font-medium">标题</th>
              <th className="px-3 py-2 text-left font-medium">客户 ID</th>
              <th className="px-3 py-2 text-left font-medium">方案 ID</th>
              <th className="px-3 py-2 text-left font-medium">负责人</th>
              <th className="px-3 py-2 text-left font-medium">状态</th>
              <th className="px-3 py-2 text-right font-medium">总金额</th>
              <th className="px-3 py-2 text-left font-medium">更新时间</th>
              <th className="px-3 py-2 text-left font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  加载中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  暂无报价
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-muted/30">
                  <td className="px-3 py-2 font-mono text-xs">{row.quoteNo}</td>
                  <td className="px-3 py-2 font-medium">{row.title}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.customerId}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.proposalId}</td>
                  <td className="px-3 py-2 text-xs">{row.ownerUserId}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${BADGE[row.status]}`}>
                      {QUOTE_STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {row.currency} {row.totalAmount}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {new Date(row.updatedAt).toLocaleString('zh-CN')}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() => navigate(`/quote-contract/quotes/${row.id}`)}
                    >
                      详情
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          共 {total} 条 · 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPageInput((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPageInput((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </Button>
        </div>
      </div>
    </div>
  );
}
